import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  NotificationsOff as DismissIcon,
} from '@mui/icons-material';
import { vendorAPI, VendorAlert, Vendor } from '../../services/vendorAPI';

interface VendorAlertsProps {
  onViewVendor?: (vendorId: number) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const VendorAlerts: React.FC<VendorAlertsProps> = ({ onViewVendor }) => {
  const [alerts, setAlerts] = useState<VendorAlert[]>([]);
  const [vendors, setVendors] = useState<Record<number, Vendor>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<VendorAlert | null>(null);
  const [dismissReason, setDismissReason] = useState('');

  useEffect(() => {
    loadAlerts();
    loadVendors();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await vendorAPI.getAlerts();
      setAlerts(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const data = await vendorAPI.getVendors();
      const vendorMap: Record<number, Vendor> = {};
      data.forEach((v: Vendor) => {
        vendorMap[v.id] = v;
      });
      setVendors(vendorMap);
    } catch (err) {
      console.error('Failed to load vendors for alert context');
    }
  };

  const handleGenerateAlerts = async () => {
    try {
      setGenerating(true);
      await vendorAPI.generateAlerts();
      await loadAlerts();
    } catch (err: any) {
      setError(err.message || 'Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      await vendorAPI.acknowledgeAlert(alertId);
      setAlerts(alerts.map(a =>
        a.id === alertId ? { ...a, status: 'acknowledged' } : a
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to acknowledge alert');
    }
  };

  const handleOpenDismiss = (alert: VendorAlert) => {
    setSelectedAlert(alert);
    setDismissReason('');
    setDismissDialogOpen(true);
  };

  const handleDismiss = async () => {
    if (!selectedAlert) return;
    try {
      await vendorAPI.resolveAlert(selectedAlert.id, dismissReason);
      setAlerts(alerts.map(a =>
        a.id === selectedAlert.id ? { ...a, status: 'resolved' } : a
      ));
      setDismissDialogOpen(false);
      setSelectedAlert(null);
    } catch (err: any) {
      setError(err.message || 'Failed to resolve alert');
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      case 'high':
        return <WarningIcon sx={{ color: '#f57c00' }} />;
      case 'medium':
        return <WarningIcon sx={{ color: '#fbc02d' }} />;
      case 'low':
        return <InfoIcon sx={{ color: '#1976d2' }} />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'acknowledged':
        return 'secondary';
      case 'dismissed':
        return 'default';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAlertTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'soc2_expiring': 'SOC 2 Expiring',
      'soc2_expired': 'SOC 2 Expired',
      'dpa_missing': 'DPA Missing',
      'review_overdue': 'Review Overdue',
      'high_risk': 'High Risk',
      'contract_expiring': 'Contract Expiring',
      'dependency_vulnerability': 'Dependency Vulnerability',
      'license_compliance': 'License Issue',
    };
    return labels[type] || type;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filterType !== 'all' && alert.alert_type !== filterType) return false;
    if (filterSeverity !== 'all' && alert.alert_severity !== filterSeverity) return false;
    if (tabValue === 0) return alert.status === 'active';
    if (tabValue === 1) return alert.status === 'acknowledged';
    if (tabValue === 2) return ['dismissed', 'resolved'].includes(alert.status);
    return true;
  });

  const activeCount = alerts.filter(a => a.status === 'active').length;
  const acknowledgedCount = alerts.filter(a => a.status === 'acknowledged').length;
  const resolvedCount = alerts.filter(a => ['dismissed', 'resolved'].includes(a.status)).length;

  const criticalCount = alerts.filter(a => a.alert_severity === 'critical' && a.status === 'active').length;
  const highCount = alerts.filter(a => a.alert_severity === 'high' && a.status === 'active').length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          {criticalCount > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${criticalCount} Critical`}
              color="error"
              variant="filled"
            />
          )}
          {highCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${highCount} High`}
              color="warning"
              variant="filled"
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={generating ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleGenerateAlerts}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Check Now'}
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Alert Type</InputLabel>
          <Select
            value={filterType}
            label="Alert Type"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="soc2_expiring">SOC 2 Expiring</MenuItem>
            <MenuItem value="soc2_expired">SOC 2 Expired</MenuItem>
            <MenuItem value="dpa_missing">DPA Missing</MenuItem>
            <MenuItem value="review_overdue">Review Overdue</MenuItem>
            <MenuItem value="high_risk">High Risk</MenuItem>
            <MenuItem value="dependency_vulnerability">Vulnerabilities</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Severity</InputLabel>
          <Select
            value={filterSeverity}
            label="Severity"
            onChange={(e) => setFilterSeverity(e.target.value)}
          >
            <MenuItem value="all">All Severities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            label={
              <Badge badgeContent={activeCount} color="error">
                <Box sx={{ pr: activeCount > 0 ? 2 : 0 }}>Active</Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={acknowledgedCount} color="primary">
                <Box sx={{ pr: acknowledgedCount > 0 ? 2 : 0 }}>Acknowledged</Box>
              </Badge>
            }
          />
          <Tab label={`Resolved (${resolvedCount})`} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <AlertTable
          alerts={filteredAlerts}
          vendors={vendors}
          onAcknowledge={handleAcknowledge}
          onDismiss={handleOpenDismiss}
          onViewVendor={onViewVendor}
          getSeverityIcon={getSeverityIcon}
          getSeverityColor={getSeverityColor}
          getStatusColor={getStatusColor}
          getAlertTypeLabel={getAlertTypeLabel}
          showActions={true}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <AlertTable
          alerts={filteredAlerts}
          vendors={vendors}
          onDismiss={handleOpenDismiss}
          onViewVendor={onViewVendor}
          getSeverityIcon={getSeverityIcon}
          getSeverityColor={getSeverityColor}
          getStatusColor={getStatusColor}
          getAlertTypeLabel={getAlertTypeLabel}
          showActions={true}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <AlertTable
          alerts={filteredAlerts}
          vendors={vendors}
          onViewVendor={onViewVendor}
          getSeverityIcon={getSeverityIcon}
          getSeverityColor={getSeverityColor}
          getStatusColor={getStatusColor}
          getAlertTypeLabel={getAlertTypeLabel}
          showActions={false}
        />
      </TabPanel>

      <Dialog open={dismissDialogOpen} onClose={() => setDismissDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide resolution notes for this alert. This will be logged for audit purposes.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Notes"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder="e.g., Risk accepted by management, Issue remediated, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDismiss}
            variant="contained"
            color="success"
            disabled={!dismissReason.trim()}
          >
            Resolve Alert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface AlertTableProps {
  alerts: VendorAlert[];
  vendors: Record<number, Vendor>;
  onAcknowledge?: (id: number) => void;
  onDismiss?: (alert: VendorAlert) => void;
  onViewVendor?: (vendorId: number) => void;
  getSeverityIcon: (severity: string) => React.ReactNode;
  getSeverityColor: (severity: string) => 'error' | 'warning' | 'info' | 'success';
  getStatusColor: (status: string) => 'default' | 'primary' | 'secondary' | 'success';
  getAlertTypeLabel: (type: string) => string;
  showActions: boolean;
}

const AlertTable: React.FC<AlertTableProps> = ({
  alerts,
  vendors,
  onAcknowledge,
  onDismiss,
  onViewVendor,
  getSeverityIcon,
  getSeverityColor,
  getStatusColor,
  getAlertTypeLabel,
  showActions,
}) => {
  if (alerts.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No alerts in this category
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={50}>Severity</TableCell>
            <TableCell>Vendor</TableCell>
            <TableCell>Alert Type</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Due Date</TableCell>
            <TableCell>Status</TableCell>
            {showActions && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts.map((alert) => {
            const vendor = vendors[alert.vendor_id];
            return (
              <TableRow key={alert.id} hover>
                <TableCell>
                  <Tooltip title={alert.alert_severity.toUpperCase()}>
                    {getSeverityIcon(alert.alert_severity) as React.ReactElement}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: onViewVendor ? 'pointer' : 'default',
                      '&:hover': onViewVendor ? { textDecoration: 'underline' } : {}
                    }}
                    onClick={() => onViewVendor?.(alert.vendor_id)}
                  >
                    {vendor?.vendor_name || 'Unknown Vendor'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {vendor?.tier ? `Tier ${vendor.tier}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getAlertTypeLabel(alert.alert_type)}
                    color={getSeverityColor(alert.alert_severity)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {alert.alert_message}
                  </Typography>
                </TableCell>
                <TableCell>
                  {alert.due_date ? (
                    <Typography variant="body2">
                      {new Date(alert.due_date).toLocaleDateString()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={alert.status}
                    color={getStatusColor(alert.status)}
                  />
                </TableCell>
                {showActions && (
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={0.5}>
                      {alert.status === 'active' && onAcknowledge && (
                        <Tooltip title="Acknowledge">
                          <IconButton
                            size="small"
                            onClick={() => onAcknowledge(alert.id)}
                            color="primary"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDismiss && (
                        <Tooltip title="Resolve">
                          <IconButton
                            size="small"
                            onClick={() => onDismiss(alert)}
                            color="success"
                          >
                            <DismissIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onViewVendor && (
                        <Tooltip title="View Vendor">
                          <IconButton
                            size="small"
                            onClick={() => onViewVendor(alert.vendor_id)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default VendorAlerts;
